import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { salesCalculateReport, calculateReportUpload } from 'lib/api/sales';
import { showSuccessSnackbar } from 'modules/snackbar';
import NewTableContainer from 'components/molecules/NewTable';
import SpanningTableContents from 'components/molecules/MbTable/SpanningTableContents';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import MbUpload from 'components/organisms/MbUpload';

import NewCommonFilter from 'components/organisms/NewCommonFilter';
import { useDispatch } from 'react-redux';
import numeral from 'numeral';

const Statistics = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  let initParams = {
    sMth: moment().add(-12, 'month').format('YYYYMM'),
    eMth: moment().format('YYYYMM'),
  };

  const [initialization, setInitialization] = useState(false);
  const [params, setParams] = useState(initParams);
  const [uploading, setUploading] = useState(false);
  const [reFetch, setReFetch] = useState(false);

  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    }
  }, [initialization]);

  /**
   * 스낵바 리덕스 값을 변경해주는 함수
   * @type {(...args: any[]) => any}
   */
  const handleSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  const customized = {
    SALES_VAT_INC: {
      makeHead: (headsMap) => {
        const result = { content: '', orderFlag: true };
        result.content = (
          <SpanningTableContents topTitle={t('모비온')} type="head">
            <div style={{ width: 100 }}>{t('매출(VAT포함)')}</div>
            <div style={{ width: 100 }}>{t('실제입금액')}</div>
            <div style={{ width: 100 }}>{t('광고대행 수수료액')}</div>
          </SpanningTableContents>
        );
        return result;
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <SpanningTableContents>
            <div style={{ width: 100 }}>
              {numeral(rowObj['SALES_VAT_INC']).format('#,##0')}
            </div>
            <div style={{ width: 100 }}>
              {numeral(rowObj['IN_POINT']).format('#,##0')}
            </div>
            <div style={{ width: 100 }}>
              {numeral(rowObj['CALCUL_FEE']).format('#,##0')}
            </div>
          </SpanningTableContents>
        );
      },
      orderFlag: false,
      headStyle: {
        borderLeft: '1px solid #e5e8eb',
        borderRight: '1px solid #e5e8eb',
      },
      bodyStyle: {
        borderLeft: '1px solid #e5e8eb',
        borderRight: '1px solid #e5e8eb',
      },
    },
    IN_POINT: {
      merged: true,
    },
    CALCUL_FEE: {
      merged: true,
    },

    MEDIA_PYMT_SINDY_FEE_INC: {
      makeHead: (headsMap) => {
        const result = { content: '', orderFlag: true };
        result.content = (
          <SpanningTableContents topTitle={t('매체')} type="head">
            <div style={{ width: 100 }}>{t('매체피+신디피(VAT포함)')}</div>
            <div style={{ width: 100 }}>{t('그로스수수료(VAT포함)')}</div>
            <div style={{ width: 100 }}>{t('실제 출금액')}</div>
          </SpanningTableContents>
        );
        return result;
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <SpanningTableContents>
            <div style={{ width: 100 }}>
              {numeral(rowObj['MEDIA_PYMT_SINDY_FEE_INC']).format('#,##0')}
            </div>
            <div style={{ width: 100 }}>
              {numeral(rowObj['MEDIA_GROSS_FEE_INC']).format('#,##0')}
            </div>
            <div style={{ width: 100 }}>
              {numeral(rowObj['RL_RCPMNY_AMT']).format('#,##0')}
            </div>
          </SpanningTableContents>
        );
      },
      orderFlag: false,
      headStyle: {
        borderLeft: '1px solid #e5e8eb',
        borderRight: '1px solid #e5e8eb',
      },
      bodyStyle: {
        borderLeft: '1px solid #e5e8eb',
        borderRight: '1px solid #e5e8eb',
      },
    },
    MEDIA_GROSS_FEE_INC: {
      merged: true,
    },
    RL_RCPMNY_AMT: {
      merged: true,
    },

    TOT_CF_AMT: {
      makeHead: (headsMap) => {
        const result = { content: '', orderFlag: true };
        result.content = (
          <SpanningTableContents topTitle={t('외부연동')} type="head">
            <div style={{ width: 100 }}>{t('총 확정매출')}</div>
            <div style={{ width: 100 }}>{t('실제 입금액')}</div>
          </SpanningTableContents>
        );
        return result;
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <SpanningTableContents>
            <div style={{ width: 100 }}>
              {numeral(rowObj['TOT_CF_AMT']).format('#,##0')}
            </div>
            <div style={{ width: 100 }}>
              {numeral(rowObj['DEPOSIT_AMT']).format('#,##0')}
            </div>
          </SpanningTableContents>
        );
      },
      orderFlag: false,
      headStyle: {
        borderLeft: '1px solid #e5e8eb',
        borderRight: '1px solid #e5e8eb',
      },
      bodyStyle: {
        borderLeft: '1px solid #e5e8eb',
        borderRight: '1px solid #e5e8eb',
      },
    },
    DEPOSIT_AMT: {
      merged: true,
    },

    SALES_BILL_VAT_SEP: {
      makeHead: (headsMap) => {
        const result = { content: '', orderFlag: true };
        result.content = (
          <SpanningTableContents topTitle={t('계산서금액(매출)')} type="head">
            <div style={{ width: 100 }}>{t('VAT별도')}</div>
            <div style={{ width: 100 }}>{t('VAT포함')}</div>
          </SpanningTableContents>
        );
        return result;
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <SpanningTableContents>
            <div style={{ width: 100 }}>
              {numeral(rowObj['SALES_BILL_VAT_SEP']).format('#,##0')}
            </div>
            <div style={{ width: 100 }}>
              {numeral(rowObj['SALES_BILL_VAT_INC']).format('#,##0')}
            </div>
          </SpanningTableContents>
        );
      },
      orderFlag: false,
      headStyle: {
        borderLeft: '1px solid #e5e8eb',
        borderRight: '1px solid #e5e8eb',
      },
      bodyStyle: {
        borderLeft: '1px solid #e5e8eb',
        borderRight: '1px solid #e5e8eb',
      },
    },
    SALES_BILL_VAT_INC: {
      merged: true,
    },

    PUR_BILL_VAT_SEP: {
      makeHead: (headsMap) => {
        const result = { content: '', orderFlag: true };
        result.content = (
          <SpanningTableContents topTitle={t('계산서금액(매입)')} type="head">
            <div style={{ width: 100 }}>{t('VAT별도')}</div>
            <div style={{ width: 100 }}>{t('VAT포함')}</div>
          </SpanningTableContents>
        );
        return result;
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <SpanningTableContents>
            <div style={{ width: 100 }}>
              {numeral(rowObj['PUR_BILL_VAT_SEP']).format('#,##0')}
            </div>
            <div style={{ width: 100 }}>
              {numeral(rowObj['PUR_BILL_VAT_INC']).format('#,##0')}
            </div>
          </SpanningTableContents>
        );
      },
      orderFlag: false,
      headStyle: {
        borderLeft: '1px solid #e5e8eb',
        borderRight: '1px solid #e5e8eb',
      },
      bodyStyle: {
        borderLeft: '1px solid #e5e8eb',
        borderRight: '1px solid #e5e8eb',
      },
    },
    PUR_BILL_VAT_INC: {
      merged: true,
    },
  };

  const handleOnReset = () => {
    setInitialization(true);
    setParams(initParams);
  };

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  const handleUpload = (status) => {
    setUploading(status);
  };

  const callUpload = async (taskId) => {
    setReFetch(true);
    handleUpload(true);
    await calculateReportUpload({ taskId: taskId })
      .then((response) => {
        const { message } = response.data;
        setReFetch(true);
        handleSnackbar({
          message: message,
          option: {
            type: 'success',
          },
        });
      })
      .catch((error) => {
        handleSnackbar({
          message: error,
          option: {
            type: 'error',
          },
        });
      })
      .finally(() => {
        handleUpload(false);
      });
  };

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          getParam={getParam}
          params={params}
          usePltfomTpCodeFilter={false}
          useDisplayFilter={false}
          useExternalFilter={false}
          useMonthDate
          useDatePresets={false}
        />
        <MbUpload
          name={'upload1'}
          label={t('세금계산서 엑셀 업로드')}
          callBack={callUpload}
          uploading={uploading}
        />
      </NewFilterWrap>
      <NewTableContainer
        dataReqPromise={salesCalculateReport}
        params={params}
        getParam={getParam}
        customized={customized}
        reFetch={reFetch}
        useSearch={false}
      />
    </>
  );
};

export default Statistics;
